import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";

export const InquiryForm = ({ property = null, compact = false }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    tenant_type: property?.target?.toLowerCase().includes("student") ? "Student" : "Professional",
    move_in_date: "",
    message: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        property_id: property?.id || null,
        property_name: property?.name || null,
      });
      toast.success("Inquiry sent. We'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", tenant_type: form.tenant_type, move_in_date: "", message: "" });
    } catch (err) {
      toast.error("Could not send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      {!compact && (
        <div>
          <div className="label-uppercase">Inquiry</div>
          <h3 className="h3 text-2xl md:text-3xl mt-1">Request a viewing</h3>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Tell us a little about you and when you'd like to move in. We typically reply within a day.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="label-uppercase mb-1.5">Full name</div>
          <input
            className="input-sharp"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="inquiry-name-input"
            required
          />
        </div>
        <div>
          <div className="label-uppercase mb-1.5">Email</div>
          <input
            type="email"
            className="input-sharp"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            data-testid="inquiry-email-input"
            required
          />
        </div>
        <div>
          <div className="label-uppercase mb-1.5">Phone</div>
          <input
            className="input-sharp"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            data-testid="inquiry-phone-input"
          />
        </div>
        <div>
          <div className="label-uppercase mb-1.5">I am a</div>
          <select
            className="input-sharp"
            value={form.tenant_type}
            onChange={(e) => set("tenant_type", e.target.value)}
            data-testid="inquiry-tenant-type"
          >
            <option value="Professional">Professional</option>
            <option value="Student">Student</option>
            <option value="Family">Family</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <div className="label-uppercase mb-1.5">Message</div>
          <textarea
            className="input-sharp"
            rows={4}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            placeholder={property ? `I'd like to view ${property.name}…` : ""}
            data-testid="inquiry-message-input"
            required
          />
        </div>
      </div>

      <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="inquiry-submit-button">
        <PaperPlaneTilt size={18} weight="fill" />
        {submitting ? "Sending…" : "Send inquiry"}
      </button>
      <p className="text-xs text-[var(--text-tertiary)]">
        By submitting you agree we may contact you about your inquiry. We never share your details.
      </p>
    </form>
  );
};
